import { useState, useEffect, useRef, memo, useCallback } from"react";
import LazyImage from"../components/LazyImage";

// 1x1 transparent placeholder — used so off-screen carousel cards don't download
// their (potentially heavy) image until they're the current or an adjacent slide.
const BLANK = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

export default function VoyageALaCarte() {
 const [formData, setFormData] = useState({
 destination:"",
 nombre_voyageurs:"",
 date_depart:"",
 date_retour:"",
 nom:"",
 prenom:"",
 telephone:"",
 email:"",
 message:""
 });
 const [isSubmitting, setIsSubmitting] = useState(false);
 const [submitStatus, setSubmitStatus] = useState(null);

 const destinations = [
 { name:"Barcelone", img1:"/assets/img/barcelone.jpg", img2:"/assets/img/barcelone2.jpg", tag:"Europe" },
 { name:"Égypte", img1:"/assets/img/egypt.jpg", img2:"/assets/img/egypt2.jpg", tag:"Afrique" },
 { name:"Malaisie", img1:"/assets/img/malaisie1.jpg", img2:"/assets/img/malaisie2.jpg", tag:"Asie" },
 { name:"Thaïlande", img1:"/assets/img/tai1.jpg", img2:"/assets/img/thai2.jpg", tag:"Asie" },
 { name:"Cuba", img1:"/assets/img/cuba1.jpg", img2:"/assets/img/cuba2.jpg", tag:"Amérique" },
 { name:"Malte", img1:"/assets/img/malta1.jpg", img2:"/assets/img/malte2.jpg", tag:"Europe" },
 { name:"Dubaï", img1:"/assets/img/dubai.png", tag:"Moyen-Orient" },
 { name:"Maroc", img1:"/assets/img/maroc.png", tag:"Afrique" },
 { name:"Afrique", img1:"/assets/img/afrique.png", tag:"Afrique" },
 { name:"Kenya", img1:"/assets/img/kenya.png", tag:"Afrique" },
 { name:"Portugal", img1:"/assets/img/portugal.png", tag:"Europe" },
 ];

 const handleInputChange = useCallback((e) => {
 const { name, value } = e.target;
 setFormData(prev => ({ ...prev, [name]: value }));
 }, []);

 const handleSubmit = async (e) => {
 e.preventDefault();
 setIsSubmitting(true);
 setSubmitStatus(null);
 const fd = new FormData();
 Object.keys(formData).forEach(k => fd.append(k, formData[k]));
 fd.append('_captcha', 'false');
 fd.append('_cc', 'manager@aviatortravel.tn,outgoing@aviatortravel.tn');
 fd.append('_subject', 'Nouvelle demande de voyage à la carte');
 fd.append('_template', 'table');
 try {
 const res = await fetch('https://formsubmit.co/booking@aviatortravel.tn', { method: 'POST', body: fd });
 if (res.ok) {
 setSubmitStatus('success');
 setFormData({ destination:"", nombre_voyageurs:"", date_depart:"", date_retour:"", nom:"", prenom:"", telephone:"", email:"", message:"" });
 } else setSubmitStatus('error');
 } catch { setSubmitStatus('error'); }
 finally { setIsSubmitting(false); }
 };

 return (
 <>
 <style>{styles}</style>

 {/* ══ BANNER ══ */}
 <div className="vac-banner">
 <div className="vac-banner__overlay" />
 <div className="vac-banner__content">
 <h1 className="vac-banner__title">Voyage à la carte</h1>
 <p className="vac-banner__sub">Vous choisissez, nous organisons.</p>
 </div>
 </div>

 {/* ══ CAROUSEL ══ */}
 <div className="vac-carousel-section">
 <Carousel destinations={destinations} />
 </div>

 {/* ══ FORM ══ */}
 <div className="vac-form-section">
 <ContactSection
 formData={formData}
 handleInputChange={handleInputChange}
 handleSubmit={handleSubmit}
 isSubmitting={isSubmitting}
 submitStatus={submitStatus}
 />
 </div>
 </>
 );
}

/* ══════════════════════════════════════════════════════
 CAROUSEL — fade CSS, zéro conflit JS
 • Toutes les cartes sont dans le DOM, empilées (position absolute)
 • La carte active est opacity:1, les autres opacity:0
 • La transition CSS fait tout le travail → fluide garantie
══════════════════════════════════════════════════════ */
const Carousel = memo(({ destinations }) => {
 const total = destinations.length;
 const [current, setCurrent] = useState(0);
 const intervalRef = useRef(null);

 const startAuto = useCallback(() => {
 clearInterval(intervalRef.current);
 intervalRef.current = setInterval(() => {
 setCurrent(c => (c + 1) % total);
 }, 4000);
 }, [total]);

 useEffect(() => {
 startAuto();
 return () => clearInterval(intervalRef.current);
 }, [startAuto]);

 const goTo = (i) => {
 setCurrent(i);
 startAuto();
 };

 const goPrev = () => { goTo((current - 1 + total) % total); };
 const goNext = () => { goTo((current + 1) % total); };

 return (
 <div className="carousel">
 <div className="carousel__header">
 <span className="carousel__tag">Nos destinations</span>
 <h2 className="carousel__title">Explorez le monde avec nous</h2>
 <p className="carousel__sub">Des expériences uniques, soigneusement sélectionnées pour vous</p>
 </div>

 <div className="carousel__stage">
 <button className="carousel__arrow carousel__arrow--left" onClick={goPrev} aria-label="Précédent">
 <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
 <polyline points="15 18 9 12 15 6"/>
 </svg>
 </button>

 {/* Stack de cartes — position absolute, fade CSS */}
 <div className="carousel__stack">
 {destinations.map((dest, i) => {
 const isNear = i === current || i === (current - 1 + total) % total || i === (current + 1) % total;
 return (
 <div
 key={dest.name}
 className={`carousel__card ${i === current ?"carousel__card--visible" :"carousel__card--hidden"}`}
 >
 <LazyImage
 src={isNear ? dest.img1 : BLANK}
 alt={dest.name}
 wrapperClassName="carousel__img"
 decoding="async"
 />
 <div className="carousel__overlay" />
 <div className="carousel__info">
 <span className="carousel__info-tag">{dest.tag}</span>
 <span className="carousel__info-name">{dest.name}</span>
 </div>
 </div>
 );
 })}

 {/* Thumbnails des voisines gauche/droite — décoratif */}
 <div className="carousel__peek carousel__peek--left">
 <LazyImage
 src={destinations[(current - 1 + total) % total].img1}
 alt=""
 wrapperClassName="carousel__peek-img"
 decoding="async"
 />
 <div className="carousel__peek-overlay" />
 </div>
 <div className="carousel__peek carousel__peek--right">
 <LazyImage
 src={destinations[(current + 1) % total].img1}
 alt=""
 wrapperClassName="carousel__peek-img"
 decoding="async"
 />
 <div className="carousel__peek-overlay" />
 </div>
 </div>

 <button className="carousel__arrow carousel__arrow--right" onClick={goNext} aria-label="Suivant">
 <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
 <polyline points="9 18 15 12 9 6"/>
 </svg>
 </button>
 </div>

 <div className="carousel__dots">
 {destinations.map((_, i) => (
 <button
 key={i}
 className={`carousel__dot${i === current ?" carousel__dot--active" :""}`}
 onClick={() => goTo(i)}
 aria-label={`Aller à ${destinations[i].name}`}
 />
 ))}
 </div>
 </div>
 );
});

/* ══════════════════════════════════════
 CONTACT SECTION
══════════════════════════════════════ */
const ContactSection = memo(({ formData, handleInputChange, handleSubmit, isSubmitting, submitStatus }) => (
 <div className="contact-box">
 <div className="contact-box__text">
 <h3 className="contact-box__title">Envie d'un voyage qui vous ressemble vraiment ?</h3>
 <p className="contact-box__lead">
 Avec Aviator Travel Service, créez un voyage à la carte, entièrement personnalisé selon vos envies, votre budget et votre rythme.
 </p>
 <div className="contact-box__details">
 <p>Que vous rêviez d'un séjour détente, d'une escapade culturelle, d'un voyage en famille ou d'une aventure entre amis, nous concevons pour vous un itinéraire unique.</p>
 <p>Grâce à notre expertise et à nos partenaires à l'international, nous vous accompagnons à chaque étape pour transformer votre projet en un voyage inoubliable.</p>
 <p className="contact-box__cta">Contactez-nous et construisons ensemble votre prochain voyage !</p>
 </div>
 </div>

 <form className="contact-box__form" onSubmit={handleSubmit} noValidate>
 <div className="form-grid">
 {/* Ligne 1 : destination · nombre */}
 <Field name="destination" placeholder="Destination souhaitée" value={formData.destination} onChange={handleInputChange} />
 <Field name="nombre_voyageurs" placeholder="Nombre de voyageurs" value={formData.nombre_voyageurs} onChange={handleInputChange} type="number" />
 {/* Ligne 2 : date de départ · date du retour */}
 <Field name="date_depart" placeholder="Date de départ" value={formData.date_depart} onChange={handleInputChange} type="date" />
 <Field name="date_retour" placeholder="Date du retour" value={formData.date_retour} onChange={handleInputChange} type="date" />
 {/* Ligne 3 : nom · prénom */}
 <Field name="nom" placeholder="Nom" value={formData.nom} onChange={handleInputChange} />
 <Field name="prenom" placeholder="Prénom" value={formData.prenom} onChange={handleInputChange} />
 {/* Ligne 4 : téléphone · email */}
 <Field name="telephone" placeholder="Téléphone" value={formData.telephone} onChange={handleInputChange} />
 <div className="field">
 <input className="field__input" type="email" name="email" placeholder="E-mail" value={formData.email} onChange={handleInputChange} />
 </div>
 {/* Ligne 5 : demande */}
 <div className="field field--full">
 <textarea className="field__input field__textarea" name="message" placeholder="Votre demande — parlez-nous de votre voyage idéal ..." value={formData.message} onChange={handleInputChange} rows={5} />
 </div>
 {submitStatus === 'success' && <div className="form-status form-status--success">✓ Votre demande a été envoyée avec succès !</div>}
 {submitStatus === 'error' && <div className="form-status form-status--error">✗ Une erreur s'est produite. Veuillez réessayer.</div>}
 <div className="form-btn-wrap">
 <button type="submit" className="form-submit" disabled={isSubmitting}>
 {isSubmitting ?"Envoi en cours..." :"Réserver Maintenant"}
 </button>
 </div>
 </div>
 </form>
 </div>
));

const Field = memo(({ type ="text", name, value, onChange, placeholder }) => (
 <div className="field">
 <input className="field__input" type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} />
 </div>
));

/* ══════════════════════════════════════
 STYLES
══════════════════════════════════════ */
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ── BANNER ── */
.vac-banner {
 position: relative;
 height: 420px;
 background: url('/assets/img/banner-voyage-carte.png') center/cover no-repeat;
 display: flex;
 align-items: center;
 justify-content: center;
 overflow: hidden;
}
.vac-banner::before {
 content: '';
 position: absolute;
 inset: 0;
 background: rgba(10, 25, 60, 0.3);
}
.vac-banner__overlay {
 position: absolute;
 inset: 0;
 background: radial-gradient(ellipse at center, transparent 50%, rgba(5,10,30,0.4) 100%);
}
.vac-banner__content {
 position: relative;
 text-align: center;
 z-index: 1;
 color: #fff;
}
.vac-banner__title {
 font-family: 'Inter', system-ui, sans-serif;
 font-size: clamp(2rem, 5vw, 3.2rem);
 font-weight: 800;
 letter-spacing: -0.02em;
 margin-bottom: 0.5rem;
 text-shadow: 0 2px 20px rgba(0,0,0,0.4);
 color: #ffffff;
}
.vac-banner__sub {
 font-family: 'Inter', system-ui, sans-serif;
 font-size: clamp(1rem, 2vw, 1.2rem);
 color: rgba(255,255,255,0.85);
}

/* ── CAROUSEL SECTION ── */
.vac-carousel-section {
 background: #f4f6fb;
 padding: 4rem 0 3rem;
}
.carousel { font-family: 'Inter', system-ui, sans-serif; }

.carousel__header {
 text-align: center;
 margin-bottom: 2.5rem;
 padding: 0 1.5rem;
}
.carousel__tag {
 display: inline-block;
 background: #00338C;
 color: #fff;
 font-size: 0.75rem;
 font-weight: 700;
 letter-spacing: 0.1em;
 text-transform: uppercase;
 padding: 0.3rem 1rem;
 border-radius: 999px;
 margin-bottom: 0.8rem;
}
.carousel__title {
 font-size: clamp(1.5rem, 3vw, 2.2rem);
 font-weight: 800;
 color: #0f172a;
 margin-bottom: 0.4rem;
}
.carousel__sub { font-size: 0.97rem; color: #64748b; }

/* Stage */
.carousel__stage {
 display: flex;
 align-items: center;
 justify-content: center;
 gap: 1.2rem;
 padding: 0 1.5rem;
}

/* Stack — centre la carte principale + thumbnails latérales */
.carousel__stack {
 position: relative;
 width: 780px;
 height: 420px;
 display: flex;
 align-items: center;
 justify-content: center;
}

/* Carte principale (fade) */
.carousel__card {
 position: absolute;
 inset: 0;
 margin: auto;
 width: 400px;
 height: 420px;
 border-radius: 20px;
 overflow: hidden;
 z-index: 2;
 transition: opacity 0.6s ease;
 box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}
.carousel__card--visible { opacity: 1; pointer-events: auto; }
.carousel__card--hidden { opacity: 0; pointer-events: none; }

.carousel__img {
 width: 100%;
 height: 100%;
 object-fit: cover;
 display: block;
}
/* Placeholder animé pendant le chargement des images (LazyImage) */
.lazy-shimmer {
 background: linear-gradient(90deg, #e2e8f0 25%, #eef2f7 37%, #e2e8f0 63%);
 background-size: 400% 100%;
 animation: lazyShimmer 1.4s ease infinite;
}
@keyframes lazyShimmer {
 0% { background-position: 100% 0; }
 100% { background-position: -100% 0; }
}
.carousel__overlay {
 position: absolute;
 inset: 0;
 background: linear-gradient(to top, rgba(5,15,40,0.78) 0%, transparent 55%);
 pointer-events: none;
}
.carousel__info {
 position: absolute;
 bottom: 1.2rem;
 left: 1.4rem;
 display: flex;
 flex-direction: column;
 gap: 0.2rem;
 pointer-events: none;
}
.carousel__info-tag {
 font-size: 0.68rem;
 font-weight: 700;
 text-transform: uppercase;
 letter-spacing: 0.1em;
 color: rgba(255,255,255,0.75);
}
.carousel__info-name {
 font-size: 1.3rem;
 font-weight: 800;
 color: #fff;
 text-shadow: 0 2px 8px rgba(0,0,0,0.5);
}

/* Thumbnails latérales — décoratives, pas de z-index conflict */
.carousel__peek {
 position: absolute;
 top: 50%;
 transform: translateY(-50%);
 width: 170px;
 height: 300px;
 border-radius: 16px;
 overflow: hidden;
 z-index: 1;
 pointer-events: none;
}
.carousel__peek--left { left: 0; }
.carousel__peek--right { right: 0; }
.carousel__peek-img {
 width: 100%;
 height: 100%;
 object-fit: cover;
 display: block;
 transition: opacity 0.6s ease;
}
.carousel__peek-overlay {
 position: absolute;
 inset: 0;
 background: rgba(15, 30, 60, 0.55);
}

/* Arrows */
.carousel__arrow {
 flex-shrink: 0;
 width: 44px;
 height: 44px;
 border-radius: 50%;
 border: 1.5px solid #d1d5db;
 background: #fff;
 color: #00338C;
 display: flex;
 align-items: center;
 justify-content: center;
 cursor: pointer;
 box-shadow: 0 2px 8px rgba(0,0,0,0.08);
 transition: all 0.2s;
 z-index: 5;
}
.carousel__arrow:hover { background: #00338C; color: #fff; border-color: #00338C; }

/* Dots */
.carousel__dots {
 display: flex;
 justify-content: center;
 gap: 0.5rem;
 margin-top: 1.6rem;
}
.carousel__dot {
 width: 8px; height: 8px;
 border-radius: 999px;
 border: none;
 background: #c7d3e8;
 cursor: pointer;
 transition: all 0.3s;
 padding: 0;
}
.carousel__dot--active { background: #00338C; width: 24px; }

/* ── FORM SECTION ── */
.vac-form-section {
 background: #fff;
 padding: 4rem 1.5rem;
}
.contact-box {
 max-width: 1100px;
 margin: 0 auto;
 background: #fff;
 border: 1px solid #e0e7f0;
 border-radius: 16px;
 padding: 3rem;
 display: grid;
 grid-template-columns: 1fr 1.2fr;
 gap: 4rem;
 align-items: start;
 box-shadow: 0 4px 24px rgba(0,0,0,0.06);
 font-family: 'Inter', system-ui, sans-serif;
}
.contact-box__title {
 font-family: 'Inter', system-ui, sans-serif;
 font-size: clamp(1.25rem, 2.5vw, 1.8rem);
 font-weight: 800;
 color: #00338C;
 line-height: 1.25;
 margin-bottom: 1.1rem;
}
.contact-box__lead {
 font-family: 'Inter', system-ui, sans-serif;
 font-size: 0.95rem;
 font-weight: 400;
 color: #01225C;
 line-height: 1.7;
 margin-bottom: 1.2rem;
}
.contact-box__details {
 display: flex;
 flex-direction: column;
 gap: 0.85rem;
}
.contact-box__details p {
 font-family: 'Inter', system-ui, sans-serif;
 font-size: 0.92rem;
 font-weight: 400;
 color: #01225C;
 line-height: 1.65;
}
.contact-box__cta {
 font-weight: 600 !important;
 color: #01225C !important;
}
.contact-box__form { width: 100%; }
.form-grid {
 display: grid;
 grid-template-columns: 1fr 1fr;
 gap: 0.85rem;
}
.field { display: flex; flex-direction: column; }
.field--full { grid-column: 1 / -1; }
.field__input {
 padding: 0.72rem 1rem;
 border: 1.5px solid #CBDEFC;
 border-radius: 10px;
 font-size: 0.88rem;
 font-family: 'Inter', system-ui, sans-serif;
 color: #01225C;
 background: #FBFDFF;
 outline: none;
 width: 100%;
 transition: border-color 0.18s, background 0.18s;
}
.field__input::placeholder { color: #4a6fa5; opacity: 1; }
.field__input:focus { border-color: #00338C; background: #eef4ff; }
.field__textarea { resize: vertical; min-height: 110px; line-height: 1.6; }
.form-status {
 grid-column: 1 / -1;
 padding: 0.7rem 1rem;
 border-radius: 8px;
 font-size: 0.87rem;
 font-weight: 600;
 text-align: center;
}
.form-status--success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }
.form-status--error { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }
.form-btn-wrap {
 grid-column: 1 / -1;
 display: flex;
 justify-content: center;
}
.form-submit {
 padding: 0.82rem 2.8rem;
 background: #00338C;
 color: #fff;
 border: none;
 border-radius: 10px;
 font-size: 0.97rem;
 font-weight: 700;
 font-family: 'Inter', system-ui, sans-serif;
 cursor: pointer;
 transition: background 0.2s, transform 0.15s;
 box-shadow: 0 4px 16px rgba(0,51,140,0.2);
}
.form-submit:hover:not(:disabled) { background: #002a75; transform: translateY(-1px); }
.form-submit:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── RESPONSIVE ── */
@media (max-width: 860px) {
 .carousel__stack { width: 100%; }
 .carousel__peek { display: none; }
 .carousel__card { width: min(85vw, 360px); }
 .contact-box { grid-template-columns: 1fr; gap: 2.5rem; padding: 2rem; }
 .form-grid { grid-template-columns: 1fr; }
}
@media (max-width: 500px) {
 .vac-banner { height: 240px; }
 .carousel__card { width: 90vw; height: 360px; }
 .form-btn-wrap { justify-content: stretch; }
 .form-submit { width: 100%; }
}
`;